/**
 * Provider Handshake Domain Types
 *
 * Unified types for all external provider connection flows.
 * Each handshake type has its own payload schema.
 *
 * @module handshake
 */

import { DbEnum } from "../../../generated/prisma-enums";
import {
  type ExternalToolOAuthHandshakePayload,
  type McpServerOAuthHandshakePayload,
  type MessengerPageConnectHandshakePayload,
  type OAuthHandshakePayload,
  type SlackInstallHandshakePayload,
  type TeamsInstallHandshakePayload
} from "../../../schemas/ProviderHandshake/payload/index";

// Create type aliases for cleaner usage
export type HandshakeType = DbEnum.HandshakeType;
export type HandshakeStatus = DbEnum.HandshakeStatus;
export type HandshakeConnectionType = DbEnum.HandshakeConnectionType;

// Re-export enum values for use as values (not just types).
export const HandshakeType = DbEnum.HandshakeType;
export const HandshakeStatus = DbEnum.HandshakeStatus;
export const HandshakeConnectionType = DbEnum.HandshakeConnectionType;

// Re-export value arrays for iteration
export const HANDSHAKE_TYPE_VALUES = DbEnum.HANDSHAKE_TYPE_VALUES;
export const HANDSHAKE_STATUS_VALUES = DbEnum.HANDSHAKE_STATUS_VALUES;
export const HANDSHAKE_CONNECTION_TYPE_VALUES = DbEnum.HANDSHAKE_CONNECTION_TYPE_VALUES;

// =============================================================================
// TYPED PAYLOADS PER HANDSHAKE TYPE
// =============================================================================

// Payload shapes are Zod schemas (single source of truth) in
// schemas/ProviderHandshake/payload/. The inferred types
// (OAuthHandshakePayload, SlackInstallHandshakePayload,
// TeamsInstallHandshakePayload) are exported from "@nexus/types" via the schemas
// barrel — imported above for the map below, not redeclared here.

// =============================================================================
// PAYLOAD TYPE MAP
// =============================================================================

/**
 * Maps HandshakeType enum values to their payload types
 * Used for type-safe payload access
 */
export interface HandshakePayloadMap {
  OAUTH_GMAIL: OAuthHandshakePayload;
  OAUTH_OUTLOOK: OAuthHandshakePayload;
  OAUTH_GOOGLE_DRIVE: OAuthHandshakePayload;
  OAUTH_GOOGLE_SHEETS: OAuthHandshakePayload;
  OAUTH_NOTION: OAuthHandshakePayload;
  OAUTH_SHAREPOINT: OAuthHandshakePayload;
  OAUTH_TEAMS_TENANT: OAuthHandshakePayload;
  OAUTH_EXTERNAL_TOOL: ExternalToolOAuthHandshakePayload;
  SLACK_APP_INSTALL: SlackInstallHandshakePayload;
  TEAMS_BOT_INSTALL: TeamsInstallHandshakePayload;
  FB_MESSENGER_PAGE_CONNECT: MessengerPageConnectHandshakePayload;
  OAUTH_MCP_SERVER: McpServerOAuthHandshakePayload;
}

/**
 * Get the payload type for a specific handshake type
 */
export type HandshakePayloadFor<T extends HandshakeType> = T extends keyof HandshakePayloadMap
  ? HandshakePayloadMap[T]
  : never;

// =============================================================================
// HANDSHAKE ENTITY
// =============================================================================

/**
 * Provider handshake entity (database record)
 */
export interface ProviderHandshake<T extends HandshakeType = HandshakeType> {
  id: string;
  type: T;
  organizationId: string;
  userId: string;
  payload: T extends keyof HandshakePayloadMap ? HandshakePayloadMap[T] : Record<string, unknown>;
  status: HandshakeStatus;
  resultConnectionId: string | null;
  resultConnectionType: HandshakeConnectionType | null;
  expiresAt: Date;
  createdAt: Date;
  completedAt: Date | null;
  errorMessage: string | null;
}

/**
 * Input for creating a new handshake
 */
export interface CreateHandshakeInput<T extends HandshakeType> {
  type: T;
  organizationId: string;
  userId: string;
  payload: HandshakePayloadFor<T>;
  /** TTL in seconds (default: 15 minutes for OAuth, 1 hour for installations) */
  ttlSeconds?: number;
}

/**
 * Input for completing a handshake
 */
export interface CompleteHandshakeInput {
  /** Handshake ID (used as state parameter in callbacks) */
  handshakeId: string;
  /** ID of the created connection */
  connectionId: string;
  /** Type of connection created */
  connectionType: HandshakeConnectionType;
}

/**
 * Input for failing a handshake
 */
export interface FailHandshakeInput {
  handshakeId: string;
  errorMessage: string;
}

// =============================================================================
// WEBSOCKET EVENTS - Re-exported from websocket module for convenience
// =============================================================================

/**
 * Handshake WebSocket events are fully typed in the websocket module:
 *
 * @example Backend usage
 * ```typescript
 * import { HandshakeEvents } from '@nexus/types/server';
 *
 * this.userEvents.emitToUser(userId, HandshakeEvents.COMPLETED, {
 *   handshakeId: handshake.id,
 *   type: 'SLACK_APP_INSTALL', // string literal for wire format
 *   connectionId,
 *   connectionType: 'API_KEY',
 *   timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend usage
 * ```typescript
 * import { HandshakeEvents, type HandshakeCompletedPayload } from '@nexus/types';
 *
 * socket.on(HandshakeEvents.COMPLETED, (payload: HandshakeCompletedPayload) => {
 *   if (payload.handshakeId === pendingHandshakeId) {
 *     toast.success('Connected!');
 *   }
 * });
 * ```
 *
 * @see packages/types/src/shared/websocket/events.constants.ts - HandshakeEvents
 * @see packages/types/src/shared/websocket/events.payloads.ts - HandshakeCompletedPayload
 */

// =============================================================================
// DEFAULT TTLs
// =============================================================================

/**
 * Default TTL for OAuth handshakes (15 minutes)
 */
export const OAUTH_HANDSHAKE_TTL_SECONDS = 15 * 60;

/**
 * Default TTL for app installation handshakes (1 hour)
 * Longer because user needs to interact with external app stores
 */
export const INSTALL_HANDSHAKE_TTL_SECONDS = 60 * 60;

/**
 * Get default TTL for a handshake type.
 *
 * Exhaustive with a `never` guard rather than a `default` that answers: a new
 * `HandshakeType` must be classified here, or it compiles as an OAuth flow.
 */
export function getDefaultHandshakeTtl(type: HandshakeType): number {
  switch (type) {
    case HandshakeType.OAUTH_GMAIL:
    case HandshakeType.OAUTH_OUTLOOK:
    case HandshakeType.OAUTH_GOOGLE_DRIVE:
    case HandshakeType.OAUTH_GOOGLE_SHEETS:
    case HandshakeType.OAUTH_NOTION:
    case HandshakeType.OAUTH_SHAREPOINT:
    case HandshakeType.OAUTH_TEAMS_TENANT:
    case HandshakeType.OAUTH_EXTERNAL_TOOL:
    case HandshakeType.OAUTH_MCP_SERVER:
      return OAUTH_HANDSHAKE_TTL_SECONDS;
    case HandshakeType.SLACK_APP_INSTALL:
    case HandshakeType.TEAMS_BOT_INSTALL:
    case HandshakeType.FB_MESSENGER_PAGE_CONNECT:
      return INSTALL_HANDSHAKE_TTL_SECONDS;
    default: {
      const unclassified: never = type;
      throw new Error(`Handshake type ${String(unclassified)} has no TTL`);
    }
  }
}

/**
 * Get the connection type created by a handshake type.
 *
 * Exhaustive with a `never` guard: a `default` answering OAUTH told every caller
 * that a flow writing `ToolCredentials` had written an `OAuthConnection`.
 */
export function getHandshakeConnectionType(type: HandshakeType): HandshakeConnectionType {
  switch (type) {
    case HandshakeType.OAUTH_GMAIL:
    case HandshakeType.OAUTH_OUTLOOK:
    case HandshakeType.OAUTH_GOOGLE_DRIVE:
    case HandshakeType.OAUTH_GOOGLE_SHEETS:
    case HandshakeType.OAUTH_NOTION:
    case HandshakeType.OAUTH_SHAREPOINT:
    case HandshakeType.OAUTH_TEAMS_TENANT:
      return HandshakeConnectionType.OAUTH;
    case HandshakeType.OAUTH_EXTERNAL_TOOL:
      return HandshakeConnectionType.TOOL_CREDENTIALS;
    case HandshakeType.SLACK_APP_INSTALL:
    case HandshakeType.TEAMS_BOT_INSTALL:
    case HandshakeType.FB_MESSENGER_PAGE_CONNECT:
      return HandshakeConnectionType.API_KEY;
    case HandshakeType.OAUTH_MCP_SERVER:
      return HandshakeConnectionType.TOOL_CREDENTIALS;
    default: {
      const unclassified: never = type;
      throw new Error(`Handshake type ${String(unclassified)} has no connection type`);
    }
  }
}
